import React, { useEffect, useState } from 'react';
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
    MinusCircleOutlined,
    PlusOutlined
} from "@ant-design/icons";
import { Divider, Form, Input, Checkbox, Space, Spin, Button as AntdButton } from "antd";
import styled from "styled-components";
import { getLocalStorageItem } from "../../utils/utilities";
import { updateProgram, getOpportunity, getProgramById, toggleSpinner, setUpdateProgram, setCreateProgram, setProgramById } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import Button from "../../components/Button";
import Select from "../../components/CustomSelect"
import {
    programDataSelector,
    updateProgramSelector,
    toggleSpinnerSelector
} from "../../redux/selectors";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";
import { getCountriesList } from "../charityProfileCreate/CharityProfileLocation"


const formItemLayout = {
    labelCol: { span: 2 },
    wrapperCol: { span: 24 },
};


const CompanyEntities = ({ className }) => {
    const baseClassName = clsx("companyEntities", className);
    const intl = useIntl();
    const [formData, setFormData] = useState({});
    const [savedClicked, setSavedClicked] = useState(false);
    const programData = useAppSelector(programDataSelector) || {};
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const showSpinner = useAppSelector(toggleSpinnerSelector);
    const programReviewDetails = JSON.parse(getLocalStorageItem('createdProgramID') || '{}');
    const { opportunityId, programID } = programReviewDetails || {};
    const updateProgramData = useAppSelector(updateProgramSelector) || {};
    const { programID: updateProgramID } = updateProgramData || {};
    const countryList = getCountriesList(intl);
    const [isChecklistValid, setIsChecklistValid] = useState(true); // State to track checklist validation

    const entitiesSupportedOptions = [
        "registered_charity",
        "community_interest_company",
        "charitable_trust",
        "unregistered_organisation",
        "charitable_company",
        "for_profit_company",
        "academic_institution",
        "government_linked_entity",
        "parish_or_community_council",
    ];

    useEffect(() => {
        if (updateProgramID && !savedClicked) {
            dispatch(setUpdateProgram(''))
            dispatch(toggleSpinner(false));
            navigate("/company/programme/create/questions");
        } else if ((updateProgramID) && savedClicked) {
            dispatch(setUpdateProgram(''))
            dispatch(toggleSpinner(false));
            dispatch(setCreateProgram(''));
            dispatch(setProgramById(''))
            navigate(`/company/programmes/review/${programID}`);
            localStorage.removeItem('createdProgramID');
            localStorage.removeItem('programReviewDetails');
        }
    }, [updateProgramID])

    useEffect(() => {
        if (programData?.programName) {
            const entitiesSupported = Array.isArray(programData?.entitiesSupported) && programData?.entitiesSupported?.filter(element => entitiesSupportedOptions?.includes(element));
            const data = {
                ...(entitiesSupported && { entitiesSupported: entitiesSupported }),
                ...(Array.isArray(programData?.opportunityCriteriaChecklist) && programData.opportunityCriteriaChecklist.length > 0 && {
                    opportunityCriteriaChecklist: programData.opportunityCriteriaChecklist.map((el) => ({ monetaryUnit: el }))
                }),
                ...(programData?.programmeCountry && {
                    programmeCountry:
                        programData?.programmeCountry.map((country) => intl.formatMessage({ id: country }))
                }),
            };
            form.setFieldsValue(data);
            setFormData(data);
        }
    }, [programData]);

    useEffect(() => {
        if (opportunityId) {
            dispatch(getOpportunity(opportunityId));
        }
    }, [opportunityId, programID]);

    useEffect(() => {
        if (programID) {
            document.documentElement.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getProgramById(programID));
        }
    }, [programID]);

    const onFinishFailed = (e) => {
        console.log("Failed:", e);
    };

    const handleProceed = (e) => {
        const formData = form.getFieldsValue();
        const checklist = formData?.opportunityCriteriaChecklist;
        if (!checklist || checklist.length === 0) {
            setIsChecklistValid(false);
            return;
        }

        setIsChecklistValid(true);

        const data = {
            entitiesSupported: [
                ...new Set([
                    ...(formData?.entitiesSupported || []),
                ])
            ],
            opportunityCriteriaChecklist: checklist?.map((el) => el?.monetaryUnit),
            programmeCountry: countryList.filter((el) => formData?.programmeCountry?.includes(el.label))?.map((el) => `country_${el.value.toLocaleLowerCase()}`) || [],
            programID
        }
        dispatch(updateProgram(data));
        dispatch(toggleSpinner(true));
    }

    const filterOption = (input, option) => {
        return option?.value?.toLowerCase()?.includes(input.toLowerCase());
    };

    const handleSaveAndExit = () => {
        setSavedClicked(true);
        setTimeout(() => {
            form.submit();
            dispatch(toggleSpinner(false));
        }, 300);
    }

    const handlePrevious = () => {
        navigate("/company/programme/create/timelines")
    }

    const onFormChange = (_, allValues) => {
        setFormData(allValues);
    };

    return (
        <div className={baseClassName}>
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <div className='contentContainer'>
                <div>
                    <StyledText as="p" variant="H1" className='mainTitle'>
                        {intl.formatMessage({ id: "eligible_entities" })}
                    </StyledText>
                    <StyledText as="p" variant="B3d" className='mainTitle'>
                        {intl.formatMessage({ id: "what_type_of_entity_is_able_to_apply_to_your_programme" })}
                    </StyledText>
                    <StyledText as="p" variant="B3" className='mainTitle'>
                        {intl.formatMessage({ id: "please_select_all_that_apply_below" })}
                    </StyledText>
                    <Form
                        {...formItemLayout}
                        labelAlign="left"
                        form={form}
                        className="entitiesFormContainer formContainer"
                        onFinish={handleProceed}
                        onFinishFailed={onFinishFailed}
                        scrollToFirstError
                        onValuesChange={onFormChange}
                    >
                        <Form.Item
                            name="entitiesSupported"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please select',
                                },
                            ]}
                        >
                            <Checkbox.Group className='checkboxGroup'>
                                {entitiesSupportedOptions?.map(el => (
                                    <div className="checkboxWrapper" key={el} style={{ marginBottom: '10px', gap: '5px' }}
                                    >
                                        <Checkbox key={el} value={el} />
                                        <span className="checkboxText">{intl.formatMessage({ id: el })}</span>
                                    </div>
                                ))}
                            </Checkbox.Group>
                        </Form.Item>
                        <StyledText as="span" variant="B3d">
                            {intl.formatMessage({ id: "eligibility_checklist" })}
                        </StyledText>
                        <StyledText as="p" variant="B3">
                            {intl.formatMessage({ id: "applicants_must_agree_to_these_before_applying" })}
                        </StyledText>
                        <Form.List name="opportunityCriteriaChecklist">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex', marginBottom: 0 }} className='optionsContainer'>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'monetaryUnit']}
                                                className="formItemInput"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: intl.formatMessage({ id: "enter" }),
                                                    },
                                                ]}
                                            >
                                                <Input
                                                    placeholder={intl.formatMessage({ id: "entity_project_must_be" })}
                                                />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#04ac9c' }} className='for' />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <AntdButton htmlType='button' className='addButton' type="dashed" onClick={() => add()} block icon={<PlusOutlined fill="red" />}>
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "add" })}
                                            </StyledText>
                                        </AntdButton>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                        {!isChecklistValid && (
                            <div className="errorText">
                                {intl.formatMessage({ id: "please_add_at_least_one_criteria" })}
                            </div>
                        )}
                        <StyledText as="span" variant="B3d">
                            {intl.formatMessage({ id: "entity_location" })}
                        </StyledText>
                        <StyledText as="p" variant="B3">
                            {intl.formatMessage({ id: "which_country_must_entities_be_located_in_leave_blank_if_any_are_accepted" })}
                        </StyledText>
                        <Form.Item
                            label=""
                            name="programmeCountry"
                            rules={[{ required: true, message: intl.formatMessage({ id: "please_select_country" }) }]}
                        >
                            <Select
                                allowClear
                                placeholder={intl.formatMessage({ id: "search_country" })}
                                type="formSelect"
                                mode="multiple"
                                showSearch
                                filterOption={filterOption}
                                options={countryList?.map((el) => ({ label: el.label, value: el.label }))}
                            />
                        </Form.Item>
                        <div className='buttonContainer'>
                            <Divider />
                            <div className='buttonContentContainer'>
                                <div className='nextAndPreviousContainer'>
                                    <Button
                                        variant="secondary"
                                        type="button"
                                        onClick={() => handlePrevious()}>
                                        {intl.formatMessage({ id: "previous" })}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        htmlType="submit"
                                    >
                                        {intl.formatMessage({ id: "next" })}
                                    </Button>
                                </div>
                                <div className="saveAndExitButton">
                                    <Button
                                        variant="secondary"
                                        htmlType="button"
                                        onClick={() => handleSaveAndExit()}>
                                        {intl.formatMessage({ id: "save_n_exit" })}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    )
}

const StyledCompanyEntities = styled(CompanyEntities)`
&.companyEntities{
    background: white;
    display: flex;
    flex-direction: column;
    padding: 20px;
    margin-top: 60px;
    justify-content: space-between;
    .flex{
        display: flex;
        gap: 40px;
    }
    .flexColumn{
        display: flex;
        flex-direction: column;
        gap: 15px;
    }
    .mainTitle{
        margin-bottom: 20px;
    }
    .errorText{
        color: #ff4d4f;
    }
    .contentContainer{
        display: flex;
        padding: 20px;
        justify-content: space-between;
        padding-bottom: 60px;
    }
    .addButton{
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }
    .formContainer{
        display: flex;
        gap: 12px;
        flex-direction: column;
    }
    .checkboxGroup{
        display: flex;
        flex-direction: row;
        width: 20vw;
    }
    .optionsContainer{
        display: flex;
        align-items: center;
        gap: 30px;
        .for{
            margin-bottom: 10px;
        }
    }
    .overlay {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 1000;
            height: 100%;
            background-color: rgba(255, 255, 255, 0.45);
        }
    .spinner {
            z-index: 2000;
            margin: auto;
            left: 50%;
            right: 50%;
            position: absolute;
            bottom: 50%;
    }

    .radioGroup {
            display: flex;
            flex-direction: column;
    }

    .buttonContainer{
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -100px;
        .buttonContentContainer{
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton{
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer{
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }
    .formItemTextField{
        .ant-form-item-control-input{
            width: 34.5vw !important;
        }
    }
    .formItemTextFieldPart{
        .ant-form-item-control-input{
            width: 16vw !important;
        } 
    }
  .ant-form-item{
    margin-bottom: 10px;
  }
  .formItemInput{
    width: 300px;
  }
}`;

export default StyledCompanyEntities;